import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import styles from './BookWordList.module.css';
import { AlertIcon, BookIcon, LoaderIcon, ArrowLeftIcon, TrashIcon } from '../components/Icons';

const BookPhraseList = () => {
  const { bookKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const initialGroup = location.state?.bookGroup;
  const initialMeta = location.state?.bookMeta || {};

  const [phrases, setPhrases] = useState(initialGroup?.items || []);
  const [loading, setLoading] = useState(!initialGroup);
  const [error, setError] = useState(initialGroup ? null : '无法加载收藏短语，请从历史页面重新进入。');
  const [expandedPhrases, setExpandedPhrases] = useState({});
  const [sentenceLoading, setSentenceLoading] = useState({});
  const [chapterMetadata, setChapterMetadata] = useState({});
  const [chapterMetadataLoading, setChapterMetadataLoading] = useState(false);
  const [chapterMetadataError, setChapterMetadataError] = useState(null);
  const [deletingPhrases, setDeletingPhrases] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const bookId = useMemo(() => {
    if (initialGroup?.bookId != null) {
      return String(initialGroup.bookId);
    }
    return bookKey ? decodeURIComponent(bookKey) : 'unknown-book';
  }, [initialGroup?.bookId, bookKey]);

  const bookTitle = initialGroup?.bookTitle || initialMeta.title || `书籍 ${bookId}`;
  const author = initialMeta.author || '';

  const handleBackToHistory = useCallback(() => {
    navigate('/history/phrases', {
      state: {
        historyView: { activeView: 'phrases' },
        refreshFavorites: hasChanges,
      },
    });
  }, [navigate, hasChanges]);

  const getPhraseKey = (phrase) => {
    const raw = phrase.id ?? phrase.favorite_id ?? phrase.favoriteId;
    return String(raw ?? '');
  };

  const fetchSentenceDetailForPhrase = useCallback(async (phrase) => {
    const sentenceIndex = Number(phrase.sentence_id) || 0;

    try {
      const response = await api.get(`/chapters/${phrase.chapter_id}`);
      const chapter = response.data;

      let contentSentences = [];
      let translationSentences = [];

      if (chapter.content) {
        try {
          const contentData = JSON.parse(chapter.content);
          if (Array.isArray(contentData?.sentences)) {
            contentSentences = contentData.sentences
              .map(item => item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else {
            const text = contentData.sentence_content || contentData.content || '';
            if (text) contentSentences = [text];
          }
        } catch (e) {
          contentSentences = (chapter.content || '')
            .split(/\n+/)
            .filter(p => p.trim());
        }
      }

      if (chapter.content_trans) {
        try {
          const transData = JSON.parse(chapter.content_trans);
          if (Array.isArray(transData)) {
            translationSentences = transData
              .map(item => item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else if (Array.isArray(transData.sentences)) {
            translationSentences = transData.sentences
              .map(item => item.sentence_content || item.content || '')
              .filter(text => text.trim());
          } else {
            const text = transData.sentence_content || transData.content || '';
            if (text) translationSentences = [text];
          }
        } catch (e) {
          translationSentences = (chapter.content_trans || '')
            .split(/\n+/)
            .filter(p => p.trim());
        }
      }

      return {
        originalText: contentSentences[sentenceIndex] || '',
        translatedText: translationSentences[sentenceIndex] || '',
        chapterTitle: chapter.title,
      };
    } catch (error) {
      console.error('获取句子详情失败:', error);
      return null;
    }
  }, []);

  const pendingChapterIds = useMemo(() => {
    if (!phrases.length) {
      return [];
    }
    const missing = new Set();
    phrases.forEach((phrase) => {
      const rawId = phrase.chapter_id;
      if (rawId === undefined || rawId === null) {
        return;
      }
      const key = String(rawId);
      if (!chapterMetadata[key]) {
        missing.add(key);
      }
    });
    return Array.from(missing);
  }, [phrases, chapterMetadata]);

  useEffect(() => {
    if (!pendingChapterIds.length) {
      setChapterMetadataLoading(false);
      return;
    }

    let isCancelled = false;

    const loadChapterMetadata = async () => {
      setChapterMetadataLoading(true);
      setChapterMetadataError(null);

      const responses = await Promise.allSettled(
        pendingChapterIds.map(async (chapterId) => {
          try {
            const response = await api.get(`/chapters/${chapterId}`);
            const chapter = response.data;

            let displayTitle = chapter.title;
            try {
              const contentData = JSON.parse(chapter.content);
              if (contentData?.chapter_title) {
                displayTitle = contentData.chapter_title;
              }
            } catch (e) {
              // JSON 格式不符合预期时忽略
            }

            const volumeInfo = chapter.volume || {};
            const novelInfo = chapter.novel || volumeInfo.novel || {};

            const volumeId = chapter.volume_id ?? volumeInfo.id;
            const volumeTitle = chapter.volume_title || volumeInfo.title;
            const volumeOrder = chapter.volume_order_index ?? volumeInfo.order_index;
            const novelId = chapter.novel_id ?? novelInfo.id;
            const novelTitle = chapter.novel_title || novelInfo.title;

            return {
              chapterId,
              data: {
                id: chapter.id,
                title: displayTitle || chapter.title,
                orderIndex: chapter.order_index,
                rawTitle: chapter.title,
                volumeId,
                volumeTitle: volumeTitle || '未命名卷',
                volumeOrder,
                novelId,
                novelTitle: novelTitle || '未命名书籍',
              }
            };
          } catch (error) {
            console.error('加载章节信息失败:', error);
            throw error;
          }
        })
      );

      if (isCancelled) {
        return;
      }

      const newEntries = {};
      let hasError = false;

      responses.forEach((result) => {
        if (result.status === 'fulfilled' && result.value?.data) {
          const key = String(result.value.chapterId);
          newEntries[key] = result.value.data;
        } else {
          hasError = true;
        }
      });

      if (Object.keys(newEntries).length) {
        setChapterMetadata((prev) => ({ ...prev, ...newEntries }));
      }

      if (hasError) {
        setChapterMetadataError('部分章节信息加载失败');
      }

      setChapterMetadataLoading(false);
    };

    loadChapterMetadata();

    return () => {
      isCancelled = true;
    };
  }, [pendingChapterIds]);

  const handleTogglePhraseExpand = async (phrase) => {
    const key = getPhraseKey(phrase);
    
    if (expandedPhrases[key]) {
      setExpandedPhrases(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return;
    }

    setSentenceLoading(prev => ({ ...prev, [key]: true }));

    const sentenceDetail = await fetchSentenceDetailForPhrase(phrase);

    if (sentenceDetail) {
      setExpandedPhrases(prev => ({
        ...prev,
        [key]: sentenceDetail
      }));
    }

    setSentenceLoading(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleDeletePhrase = async (phrase) => {
    const key = getPhraseKey(phrase);
    const phraseId = phrase.id;

    if (!phraseId) {
      return;
    }

    if (!window.confirm(`确定要删除短语"${phrase.phrase}"吗？`)) {
      return;
    }

    setDeletingPhrases(prev => ({ ...prev, [key]: true }));

    try {
      await api.delete(`/favorite-phrases/${phraseId}`);
      setPhrases(prev => prev.filter(p => getPhraseKey(p) !== key));
      setHasChanges(true);
    } catch (error) {
      console.error('删除收藏短语失败:', error);
      alert('删除失败，请稍后重试');
    } finally {
      setDeletingPhrases(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleGoToChapter = (phrase) => {
    if (!phrase.chapter_id) {
      return;
    }
    const meta = chapterMetadata[String(phrase.chapter_id)];
    const novelId = meta?.novelId || bookId;
    navigate(`/read/${novelId}/${phrase.chapter_id}`, {
      state: {
        from: `/history/phrases/${bookKey}`,
      },
    });
  };

  const groupedPhrases = useMemo(() => {
    const groups = new Map();

    phrases.forEach(phrase => {
      const chapterKey = String(phrase.chapter_id);
      if (!groups.has(chapterKey)) {
        groups.set(chapterKey, []);
      }
      groups.get(chapterKey).push(phrase);
    });

    const result = Array.from(groups.entries()).map(([chapterKey, chapterPhrases]) => {
      const meta = chapterMetadata[chapterKey];
      return {
        chapterId: chapterKey,
        chapterTitle: meta?.title || `章节 ${chapterKey}`,
        volumeTitle: meta?.volumeTitle || '',
        phrases: chapterPhrases,
      };
    });

    result.sort((a, b) => {
      const metaA = chapterMetadata[a.chapterId];
      const metaB = chapterMetadata[b.chapterId];
      const orderA = metaA?.orderIndex ?? Number.MAX_SAFE_INTEGER;
      const orderB = metaB?.orderIndex ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    return result;
  }, [phrases, chapterMetadata]);

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingContainer}>
          <LoaderIcon size={48} />
          <p>加载收藏短语中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.errorContainer}>
          <AlertIcon size={48} />
          <h3>加载失败</h3>
          <p>{error}</p>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToHistory}
          >
            返回历史记录
          </button>
        </div>
      </div>
    );
  }

  if (!phrases.length) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            onClick={handleBackToHistory}
            aria-label="返回历史记录"
          >
            <ArrowLeftIcon size={20} />
          </button>
          <div className={styles.titleSection}>
            <h1 className={styles.bookTitle}>{bookTitle}</h1>
            {author && <p className={styles.bookAuthor}>{author}</p>}
          </div>
        </div>
        <div className={styles.emptyState}>
          <BookIcon size={48} />
          <h3>该书籍暂无收藏短语</h3>
          <p>在阅读AI笔记时点击短语旁的收藏按钮即可收藏</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBackToHistory}
          aria-label="返回历史记录"
        >
          <ArrowLeftIcon size={20} />
        </button>
        <div className={styles.titleSection}>
          <h1 className={styles.bookTitle}>{bookTitle}</h1>
          {author && <p className={styles.bookAuthor}>{author}</p>}
          <p className={styles.statsText}>共 {phrases.length} 个收藏短语</p>
        </div>
      </div>

      {chapterMetadataError && (
        <div className={styles.warningBanner}>
          <AlertIcon size={16} />
          <span>{chapterMetadataError}</span>
        </div>
      )}

      <div className={styles.contentContainer}>
        {groupedPhrases.map((group) => (
          <div key={group.chapterId} className={styles.chapterGroup}>
            <div className={styles.chapterHeader}>
              <h2 className={styles.chapterTitle}>
                {group.volumeTitle && <span className={styles.volumeLabel}>{group.volumeTitle}</span>}
                {group.chapterTitle}
              </h2>
              <span className={styles.phraseCount}>{group.phrases.length} 个短语</span>
            </div>

            <div className={styles.phrasesList}>
              {group.phrases.map((phrase) => {
                const key = getPhraseKey(phrase);
                const isExpanded = expandedPhrases[key];
                const isLoading = sentenceLoading[key];
                const isDeleting = deletingPhrases[key];

                return (
                  <div key={key} className={styles.phraseItem}>
                    <div className={styles.phraseHeader}>
                      <div className={styles.phraseMainContent}>
                        <div className={styles.phraseText}>{phrase.phrase}</div>
                        {phrase.phrase_content && (
                          <div className={styles.phraseContent}>
                            {phrase.phrase_content.split('\n').map((line, idx) => (
                              <div key={idx} className={styles.phraseContentLine}>
                                {line}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className={styles.phraseActions}>
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => handleTogglePhraseExpand(phrase)}
                          disabled={isLoading}
                          title={isExpanded ? '收起句子' : '查看句子'}
                        >
                          {isLoading ? <LoaderIcon size={16} /> : isExpanded ? '收起' : '查看句子'}
                        </button>
                        <button
                          type="button"
                          className={styles.actionButton}
                          onClick={() => handleGoToChapter(phrase)}
                          title="跳转到章节"
                        >
                          跳转
                        </button>
                        <button
                          type="button"
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => handleDeletePhrase(phrase)}
                          disabled={isDeleting}
                          title="删除"
                        >
                          {isDeleting ? <LoaderIcon size={16} /> : <TrashIcon size={16} />}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className={styles.sentenceDetail}>
                        <div className={styles.sentenceRow}>
                          <span className={styles.sentenceLabel}>中文：</span>
                          <span className={styles.sentenceText}>{isExpanded.originalText || '无'}</span>
                        </div>
                        <div className={styles.sentenceRow}>
                          <span className={styles.sentenceLabel}>英文：</span>
                          <span className={styles.sentenceText}>{isExpanded.translatedText || '无'}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookPhraseList;

